import { useState } from 'react';
import { useNavigate } from 'react-router';
import { DEPARTMENT_LABEL } from '@/domain';
import type { ReportRange } from '@/application/ports/ReportRepository';
import { useStaffReport } from '@/application/usecases/useBoard';
import { useSession } from '@/application/usecases/useSession';
import { formatDuration } from '@/application/timer/timerStore';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { SkeletonCards } from '@/ui/feedback/Loading';
import styles from './PerfScreen.module.css';

/**
 * My performance. Section 5.2 and section 4.2.
 *
 * Nobody holds it as the lab's matrix starts: technicians do not see their own
 * productivity numbers, which was an explicit client instruction, item 6 of
 * section 17. It is the matrix's to give all the same. The lab's answer of
 * 11 October 2026 was that the tick always wins, so whoever is given this
 * screen, a technician included, reads their own figures here.
 *
 * The figures come from the same `/reports/staff` the manager's screen reads,
 * filtered to the signed in user. One endpoint, one definition of a completed
 * task, so the two screens can never disagree about the same person.
 */
/** All time first, and chosen when the screen opens: the lab's request of 17 October 2026, item 4. */
const RANGES: ReadonlyArray<{ key: ReportRange; label: string }> = [
  { key: 'all', label: strings.reports.rangeAll },
  { key: 'today', label: strings.reports.rangeToday },
  { key: 'week', label: strings.reports.rangeWeek },
  { key: 'month', label: strings.reports.rangeMonth },
];

export function PerfScreen() {
  const session = useSession();
  const navigate = useNavigate();
  const [range, setRange] = useState<ReportRange>('all');
  const report = useStaffReport(range, true);
  // Each figure opens the person's own record: what they hold now and what they did last. Item 1.
  const toProfile = () => void navigate('/profile');

  const me = session.data?.user;
  const mine = report.data?.rows.find((row) => row.userId === me?.id);

  return (
    <div>
      <div className={styles.head}>
        <span className={styles.lbl}>{strings.perf.label}</span>
        <h2>{me?.name ?? ''}</h2>
        <span className={styles.hint}>
          {me === undefined
            ? strings.perf.hint
            : `${me.title} · ${DEPARTMENT_LABEL[me.department]}`}
        </span>
      </div>

      <div className={styles.seg} role="group" aria-label={strings.reports.rangeLabel}>
        {RANGES.map((option) => (
          <button
            key={option.key}
            type="button"
            className={range === option.key ? styles.on : undefined}
            aria-pressed={range === option.key}
            onClick={() => setRange(option.key)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {report.isError && <p className={styles.hint}>{failureWords(report.error, strings.errors.server)}</p>}
      {report.isPending && <SkeletonCards cards={4} label={strings.common.loadingReports} />}

      {mine === undefined && !report.isPending && !report.isError && (
        <div className={styles.empty}>
          <h3>{strings.perf.empty}</h3>
          <p>{strings.perf.emptyDetail}</p>
        </div>
      )}

      {mine !== undefined && (
        <>
          <div className={styles.stats}>
            <Stat label={strings.perf.tasksDone} value={String(mine.tasksCompleted)} onPress={toProfile} />
            <Stat label={strings.perf.teethWorked} value={String(mine.teethWorked)} onPress={toProfile} />
            {/* Only for somebody who also takes cases in. Request of 3 October 2026, section 4. */}
            {mine.jobOrdersCreated > 0 && (
              <Stat label={strings.perf.jobOrdersCreated} value={String(mine.jobOrdersCreated)} onPress={toProfile} />
            )}
            <Stat label={strings.perf.timeWorked} value={formatDuration(mine.activeSeconds)} onPress={toProfile} />
            <Stat
              label={strings.perf.averagePerTask}
              value={formatDuration(Math.round(mine.averageSecondsPerTask))}
              onPress={toProfile}
            />
            <Stat label={strings.perf.onTime} value={`${mine.onTimePercent}%`} onPress={toProfile} />
          </div>

          <div className={styles.card}>
            <div className={styles.utilHead}>
              <span>{strings.perf.utilisation}</span>
              <span className={styles.percent}>{mine.utilisationPercent}%</span>
            </div>
            <span className={styles.bar}>
              <i style={{ width: `${Math.min(100, mine.utilisationPercent)}%` }} />
            </span>
            <p className={styles.foot}>
              {formatDuration(mine.activeSeconds)} {strings.perf.ofShift}
            </p>
          </div>

          <p className={styles.privacy}>{strings.perf.hint}</p>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, onPress }: { label: string; value: string; onPress: () => void }) {
  return (
    <button type="button" className={`${styles.stat} ${styles.statPress}`} onClick={onPress}>
      <div className={styles.l}>{label}</div>
      <div className={styles.v}>{value}</div>
    </button>
  );
}
